/** @jsx jsx */
import React, { useCallback, useMemo, type Ref } from 'react';
import { jsx } from '@compiled/react';
import { PlansUpsellCard } from '@jira/growth__feature-gates/src/PlansUpsellCard.tsx';
import PremiumFeatureGate from '@jira/navigation-apps__atlassian-navigation/src/ui/plans/menu/premium-feature-gate/index.tsx';
import {
	L1_MENU_ID,
	type Nav4DragAndDropMenuItemProps,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { useConfigurableMessageAndIconMap } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import { PremiumUpgradeModalEntryPointButton } from '@jira/growth__premium-upgrade-modal/src/ui/premium-upgrade-modal-entry-point-button/index.tsx';
import {
	FlyoutMenuItem,
	FlyoutMenuItemContent,
	FlyoutMenuItemTrigger,
} from '@atlaskit/side-nav-items/flyout-menu-item';
import LinkButton from '@atlaskit/button/link';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { FormattedMessage } from '@atlassian/jira-intl';
import {
	fireUIAnalytics,
	FireScreenAnalytics,
	ContextualAnalyticsData,
} from '@atlassian/jira-product-analytics-bridge';

const LEARN_MORE_LINK =
	'https://www.atlassian.com/software/jira/guides/advanced-roadmaps/overview#what-are-advanced-roadmaps';

interface Props extends Nav4DragAndDropMenuItemProps {}

const PremiumFeatureGateNew = (): JSX.Element => {
	const onClickCTA = useCallback((_: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'plansNavigationMenuItem', {
			level: 2,
			componentType: 'fixedItemAction',
			parentItemId: 'plans',
			itemId: 'tryItFree',
		});
	}, []);

	const onClickLearnMore = useCallback((_: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'plansNavigationMenuItem', {
			level: 2,
			componentType: 'fixedItemAction',
			parentItemId: 'plans',
			itemId: 'readTheGuide',
		});
	}, []);

	return (
		<ContextualAnalyticsData sourceName="plansPremiumFeatureGate" sourceType="Modal">
			<PlansUpsellCard
				primaryButton={
					<PremiumUpgradeModalEntryPointButton onClick={onClickCTA} appearance="primary">
						<FormattedMessage
							id="navigation-apps-sidebar-nav4-sidebars-content-plans.ui.plans.premium-upsell-feature-gate.try-it-free"
							defaultMessage="Try it free"
							description="Label for the primary CTA button on the Plans upsell card to start a free trial"
						/>
					</PremiumUpgradeModalEntryPointButton>
				}
				secondaryButton={
					<LinkButton
						href={LEARN_MORE_LINK}
						target="_blank"
						appearance="subtle"
						onClick={onClickLearnMore}
					>
						<FormattedMessage
							id="navigation-apps-sidebar-nav4-sidebars-content-plans.ui.plans.premium-upsell-feature-gate.learn-more"
							defaultMessage="Learn more"
							description="Label for the secondary button on the Plans upsell card linking to the advanced roadmaps guide"
						/>
					</LinkButton>
				}
			/>
			<FireScreenAnalytics />
		</ContextualAnalyticsData>
	);
};

const FeatureGateContent = () => {
	const attributes = useMemo(
		() => ({
			'experiment-twcg-1814': expVal(
				'luna-twcg-1814-modernise-jira-upsell-ui',
				'cohort',
				'not-enrolled',
			),
		}),
		[],
	);

	return (
		<ContextualAnalyticsData attributes={attributes}>
			{expVal('luna-twcg-1814-modernise-jira-upsell-ui', 'cohort', 'not-enrolled') !==
			'variation' ? (
				<PremiumFeatureGate />
			) : (
				<PremiumFeatureGateNew />
			)}
		</ContextualAnalyticsData>
	);
};

export const Nav4PlansPremiumUpsellFeatureGate = (props: Props): JSX.Element => {
	const { draggableRef, dropIndicator, hasDragIndicator, isDragging, visualContentRef } = props;
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.PLANS);
	// oxlint-disable-next-line typescript/consistent-type-assertions
	const dragRef = draggableRef as Ref<HTMLButtonElement>;

	return (
		<FlyoutMenuItem>
			<FlyoutMenuItemTrigger
				interactionName="nav4-sidebar-plans-premium-upsell"
				elemBefore={icon}
				hasDragIndicator={hasDragIndicator}
				ref={dragRef}
				isDragging={isDragging}
				dropIndicator={dropIndicator}
				visualContentRef={visualContentRef}
			>
				{formattedMessage}
			</FlyoutMenuItemTrigger>
			<FlyoutMenuItemContent
				maxHeight={fg('platform_dst_nav4_flyout_menu_slots_close_button') ? 460 : undefined}
			>
				<FeatureGateContent />
			</FlyoutMenuItemContent>
		</FlyoutMenuItem>
	);
};
